import { LIMITS, parseCsv, type CellValue, type ColumnType, type DataRow, type DatasetColumn } from '@tallyframe/core';

/**
 * Local CSV coercion, used only when the uploader corrects the inferred schema.
 *
 * `POST /api/reports/from-csv` takes a file and a title (contracts §4); it has
 * nowhere to put a corrected column list, and `preview-csv` returns at most 20
 * sample rows, so the corrected rows cannot come back from the preview either.
 * When corrections exist the browser therefore takes the explicit route —
 * `POST /api/datasets`, `POST /api/datasets/:id/snapshots`, `POST /api/reports`
 * — which does accept them, and re-reads the file it already holds.
 *
 * The cell rules deliberately match the server's inference module: a blank cell
 * is `null` rather than `0` or `""`, a non-finite number is `null`, and dates
 * are kept verbatim rather than reformatted into something the source never said.
 */

const TRUE_WORDS = new Set(['true', 'yes']);

function coerce(raw: string, type: ColumnType): CellValue {
  const value = raw.trim();
  if (value.length === 0) return null;
  switch (type) {
    case 'integer':
    case 'number': {
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    }
    case 'boolean':
      return TRUE_WORDS.has(value.toLowerCase());
    case 'date':
    case 'string':
      return raw;
  }
}

export interface CoercedCsv {
  rows: DataRow[];
  rowCount: number;
}

/**
 * Re-reads an uploaded CSV against a (possibly corrected) column list.
 *
 * Column order is the header order the preview reported, which is how a
 * corrected column keeps pointing at the same CSV field.
 */
export function coerceCsvRows(text: string, columns: DatasetColumn[]): CoercedCsv {
  const table = parseCsv(text);
  const dataRows = table.slice(1);
  const rows: DataRow[] = dataRows.map((raw) => {
    const row: DataRow = {};
    columns.forEach((column, index) => {
      row[column.id] = coerce(raw[index] ?? '', column.type);
    });
    return row;
  });
  return { rows, rowCount: rows.length };
}

/** Rough guard against building a JSON body the server will reject outright.
 *  Better a clear message before the upload than a `LIMIT_EXCEEDED` after it. */
export function estimateJsonBytes(rows: DataRow[]): number {
  return new TextEncoder().encode(JSON.stringify(rows)).byteLength;
}

export const MAX_SNAPSHOT_JSON_BYTES = Math.floor(LIMITS.maxBodyBytes * 0.9);
